var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo, useState } from "react";
import { useAuth } from "../../auth/authProvider";
import { useGraphProfile } from "../../hooks/useGraphProfile";
import { useSignaturePrefs } from "../../hooks/useSignaturePrefs";
import { buildSignatureHtml, createSignaturePhotoCidFilename, getSignatureInlineImages, getSignaturePhotoInlineImage, } from "../../signature/signatureBuilder";
import { injectSignature } from "../../signature/signatureInjector";
import SignaturePreview from "./SignaturePreview";
import SignatureEditor from "./SignatureEditor";
export default function App() {
    const { accessToken, displayName, upn, loading: authLoading, error: authError, retry } = useAuth();
    const { graphUser, photoDataUri, loading: profileLoading, error: profileError, refresh } = useGraphProfile(accessToken);
    const { prefs, updatePrefs, resetPrefs } = useSignaturePrefs(upn);
    const [inserting, setInserting] = useState(false);
    const [insertSuccess, setInsertSuccess] = useState(false);
    const [insertError, setInsertError] = useState(null);
    const signatureHtml = useMemo(() => {
        if (!graphUser || !photoDataUri)
            return null;
        return buildSignatureHtml(graphUser, photoDataUri, prefs);
    }, [graphUser, photoDataUri, prefs]);
    function handleInsert() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!graphUser || !photoDataUri)
                return;
            setInserting(true);
            setInsertError(null);
            setInsertSuccess(false);
            try {
                const photoCidFilename = createSignaturePhotoCidFilename(photoDataUri);
                const htmlForInsert = buildSignatureHtml(graphUser, photoDataUri, prefs, {
                    iconMode: "cid",
                    photoCidFilename,
                });
                yield injectSignature(htmlForInsert, {
                    inlineImages: [
                        ...getSignatureInlineImages(prefs),
                        getSignaturePhotoInlineImage(photoDataUri, photoCidFilename),
                    ],
                });
                setInsertSuccess(true);
                setTimeout(() => setInsertSuccess(false), 3000);
            }
            catch (err) {
                setInsertError(err instanceof Error ? err.message : "Failed to insert signature");
            }
            finally {
                setInserting(false);
            }
        });
    }
    // --- Auth loading ---
    if (authLoading) {
        return (React.createElement("div", { style: styles.container },
            React.createElement("div", { style: styles.centered },
                React.createElement("div", { style: styles.spinner }),
                React.createElement("p", { style: styles.mutedText }, "Signing you in..."))));
    }
    // --- Auth error ---
    if (authError || !accessToken) {
        return (React.createElement("div", { style: styles.container },
            React.createElement("div", { style: styles.card },
                React.createElement("h2", { style: styles.errorTitle }, "Sign-in required"),
                React.createElement("p", { style: styles.mutedText }, authError !== null && authError !== void 0 ? authError : "Could not acquire an access token."),
                React.createElement("button", { style: styles.primaryBtn, onClick: retry }, "Try Again"))));
    }
    return (React.createElement("div", { style: styles.container },
        React.createElement("div", { style: styles.header },
            React.createElement("span", { style: styles.headerTitle }, "Email Signature"),
            displayName && React.createElement("span", { style: styles.headerUser }, displayName)),
        profileLoading && (React.createElement("div", { style: styles.centered },
            React.createElement("div", { style: styles.spinner }),
            React.createElement("p", { style: styles.mutedText }, "Loading your profile from Entra ID..."))),
        profileError && !profileLoading && (React.createElement("div", { style: styles.card },
            React.createElement("p", { style: styles.errorText }, profileError),
            React.createElement("button", { style: styles.secondaryBtn, onClick: refresh }, "Retry"))),
        signatureHtml && !profileLoading && (React.createElement(React.Fragment, null,
            React.createElement("div", { style: styles.section },
                React.createElement("span", { style: styles.sectionLabel }, "Preview"),
                React.createElement(SignaturePreview, { html: signatureHtml })),
            React.createElement("div", { style: styles.section },
                React.createElement("span", { style: styles.sectionLabel }, "Settings"),
                React.createElement(SignatureEditor, { prefs: prefs, onUpdate: updatePrefs })),
            React.createElement("div", { style: styles.actions },
                React.createElement("button", { style: inserting ? styles.disabledBtn : styles.primaryBtn, onClick: handleInsert, disabled: inserting }, inserting ? "Inserting..." : "Insert Signature"),
                React.createElement("button", { style: styles.ghostBtn, onClick: refresh, disabled: profileLoading }, "Refresh Profile")),
            insertSuccess && (React.createElement("div", { style: styles.successBanner }, "Signature inserted successfully.")),
            insertError && (React.createElement("div", { style: styles.errorBanner }, insertError))))));
}
const styles = {
    container: {
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: 13,
        color: "#323130",
        padding: "0 0 24px 0",
        minHeight: "100vh",
        backgroundColor: "#faf9f8",
    },
    header: {
        backgroundColor: "#0078d4",
        color: "#ffffff",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    headerTitle: {
        fontWeight: "bold",
        fontSize: 15,
    },
    headerUser: {
        fontSize: 12,
        opacity: 0.85,
    },
    centered: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 16px",
        gap: 12,
    },
    card: {
        margin: "16px",
        padding: "16px",
        backgroundColor: "#ffffff",
        borderRadius: 4,
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
    },
    section: {
        margin: "12px 16px 0",
    },
    sectionLabel: {
        display: "block",
        fontSize: 11,
        fontWeight: "bold",
        textTransform: "uppercase",
        color: "#605e5c",
        marginBottom: 6,
        letterSpacing: "0.04em",
    },
    actions: {
        margin: "16px 16px 0",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
    },
    primaryBtn: {
        backgroundColor: "#0078d4",
        color: "#ffffff",
        border: "none",
        borderRadius: 4,
        padding: "8px 16px",
        fontSize: 13,
        cursor: "pointer",
        fontFamily: "Arial, sans-serif",
    },
    secondaryBtn: {
        backgroundColor: "#ffffff",
        color: "#0078d4",
        border: "1px solid #0078d4",
        borderRadius: 4,
        padding: "8px 16px",
        fontSize: 13,
        cursor: "pointer",
        fontFamily: "Arial, sans-serif",
    },
    ghostBtn: {
        backgroundColor: "transparent",
        color: "#323130",
        border: "1px solid #d0d0d0",
        borderRadius: 4,
        padding: "8px 16px",
        fontSize: 13,
        cursor: "pointer",
        fontFamily: "Arial, sans-serif",
    },
    disabledBtn: {
        backgroundColor: "#c7e0f4",
        color: "#ffffff",
        border: "none",
        borderRadius: 4,
        padding: "8px 16px",
        fontSize: 13,
        cursor: "not-allowed",
        fontFamily: "Arial, sans-serif",
    },
    successBanner: {
        margin: "8px 16px 0",
        padding: "8px 12px",
        backgroundColor: "#dff6dd",
        color: "#107c10",
        borderRadius: 4,
        fontSize: 13,
    },
    errorBanner: {
        margin: "8px 16px 0",
        padding: "8px 12px",
        backgroundColor: "#fde7e9",
        color: "#a4262c",
        borderRadius: 4,
        fontSize: 13,
    },
    errorTitle: {
        margin: "0 0 8px",
        fontSize: 15,
        color: "#a4262c",
    },
    errorText: {
        margin: "0 0 12px",
        color: "#a4262c",
        fontSize: 13,
    },
    mutedText: {
        color: "#605e5c",
        fontSize: 13,
        margin: 0,
    },
    spinner: {
        width: 28,
        height: 28,
        border: "3px solid #c7e0f4",
        borderTopColor: "#0078d4",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
    },
};
